import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Steps, Button, Upload, App } from 'antd';
import { useNavigate } from 'react-router-dom';
import { createCatalogOrder, updateCatalogOrderWithAvailableAndOrderableItems, updateCatalogOrderForAddition } from '../../services/api/catalogOrdersApi';
import StepperModal from '../Components/Modals/StepperModal';
import UploadFileStep from './Steps/UploadFileStep';
import ReviewSummaryStep from './Steps/ReviewSummaryStep';
import SelectAddStep from './Steps/SelectAddStep';
import ConfirmCloseModal from './Modals/ConfirmCloseModal';
import ConfirmAddModal from './Modals/ConfirmAddModal';
import SuccessProcessingModal from './Modals/SuccessProcessingModal';

const { Step } = Steps;

/**
 * Modal component for bulk adding assets to a catalogue
 * 
 * @param {Object} props - Component properties
 * @param {boolean} props.visible - Determines if the modal is visible
 * @param {Function} props.onCancel - Function called when the modal is closed
 * @param {Array} props.catalogues - List of available catalogues
 * @param {boolean} props.loading - Indicates if catalogues are loading
 * @param {string} props.initialCatalogId - ID of the catalog to pre-select
 * @param {boolean} props.resetStepper - Flag to reset the stepper to initial state
 * @param {Function} props.onResetComplete - Callback when reset is complete
 * @param {string} props.clientId - Client ID for the catalog order
 * @param {Function} props.getClientId - Function to get client ID when needed
 */
const BulkAddModal = ({
    visible,
    onCancel,
    onDirectClose = null,
    catalogues = [],
    loading = false,
    initialCatalogId = null,
    resetStepper = false,
    onResetComplete = () => { },
    clientId = null,
    getClientId = null
}) => {
    const { message } = App.useApp();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [fileList, setFileList] = useState([]);
    const [uploadedData, setUploadedData] = useState([]);
    const [summary, setSummary] = useState({
        total: 0,
        valid: 0,
        invalid: 0,
        details: []
    });
    const [selectedCatalogue, setSelectedCatalogue] = useState(initialCatalogId);
    const [selectedLicense, setSelectedLicense] = useState(null);
    const [processingUpload, setProcessingUpload] = useState(false);
    const [parsingFile, setParsingFile] = useState(false);
    const [fileProcessed, setFileProcessed] = useState(false);
    const [catalogOrderId, setCatalogOrderId] = useState(null);
    const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);
    const [confirmAddVisible, setConfirmAddVisible] = useState(false);
    const [successModalVisible, setSuccessModalVisible] = useState(false);
    const [successModalProductsCount, setSuccessModalProductsCount] = useState(0);
    const [selectedProductsCount, setSelectedProductsCount] = useState(0);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [internalVisible, setInternalVisible] = useState(true);
    const [savingToOrder, setSavingToOrder] = useState(false);

    // Effect to reset the stepper when the resetStepper prop changes
    useEffect(() => {
        if (resetStepper && visible) {
            // Reset all state to initial values
            setCurrentStep(0);
            setFileList([]);
            setUploadedData([]);
            setSummary({
                total: 0,
                valid: 0,
                invalid: 0,
                details: []
            });
            // Keep the selected catalog if initialCatalogId is provided
            setSelectedCatalogue(initialCatalogId);
            setSelectedLicense(null);
            // Form reset will be handled by SelectAddStep when it mounts
            // Reset file processed state
            setFileProcessed(false);
            // Reset catalog order ID
            setCatalogOrderId(null);
            // Reset confirm close modal
            setConfirmCloseVisible(false);
            // Reset processing states
            setProcessingUpload(false);
            setParsingFile(false);
            // Reset selections
            setSelectedRowKeys([]);
            setSelectedProductsCount(0);
            setSelectedProducts([]);
            // Reset internal visibility
            setInternalVisible(true);
            // Reset saving flag
            setSavingToOrder(false);

            // Notify parent that reset is complete
            onResetComplete();
        }
    }, [resetStepper, visible, initialCatalogId, onResetComplete]);

    // Effect to reset internal visibility when the modal becomes visible
    useEffect(() => {
        if (visible) {
            setInternalVisible(true);
        }
    }, [visible]);

    // Configuration for file upload
    const uploadProps = {
        name: 'file',
        multiple: false,
        accept: '.csv, .xlsx, .xls',
        fileList,
        beforeUpload: (file) => {
            // Verify that the file is in a supported format
            const isValidFormat = file.type === 'text/csv' ||
                file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel';

            if (!isValidFormat) {
                message.error('You can only upload CSV or Excel files!');
                return Upload.LIST_IGNORE;
            }

            // Add the file to the list
            setFileList([file]);

            // Reset file processed state when a new file is uploaded
            setFileProcessed(false);

            // Prevent automatic upload
            return false;
        },
        onRemove: () => {
            setFileList([]);
            setUploadedData([]);
            setSummary({
                total: 0,
                valid: 0,
                invalid: 0,
                details: []
            });
            setFileProcessed(false);
            setCatalogOrderId(null);
        }
    };

    // Function to handle file upload (creates catalog order)
    const handleFileUploaded = async (file) => {
        // Skip creating order if we already have one
        if (catalogOrderId) {
            return;
        }

        try {
            // Ottieni il clientId dalla funzione se disponibile, altrimenti usa il prop diretto
            const currentClientId = getClientId ? await getClientId() : clientId;

            if (!currentClientId || currentClientId === '' || currentClientId === 'null') {
                console.warn('No valid client ID provided, cannot create catalog order. ClientId:', currentClientId);
                message.warning('Client ID not available. Please try logging in again.');
                return;
            }

            const orderId = await createCatalogOrder(currentClientId, selectedCatalogue);
            setCatalogOrderId(orderId);
        } catch (error) {
            console.error('Error creating catalog order:', error);
            message.error('Error creating catalog order');
        }
    };

    // Function to handle file processing result
    const handleFileProcessed = async (success, data) => {
        if (success) {
            setFileProcessed(true);

            // Aggiorna i dati caricati con i risultati dall'API
            if (data.results) {
                setUploadedData(data.results);

                // Calcola il riassunto basato sui nuovi campi result
                const orderableItems = data.results.filter(item => item.result === 'Orderable');
                const availableItems = data.results.filter(item => item.result === 'Available');
                const errorItems = data.results.filter(item => item.result === 'Error');
                const otherItems = data.results.filter(item => !['Orderable', 'Available', 'Error'].includes(item.result));

                setSummary({
                    total: data.results.length,
                    orderable: orderableItems.length,
                    available: availableItems.length,
                    error: errorItems.length,
                    other: otherItems.length,
                    details: {
                        orderable: orderableItems,
                        available: availableItems,
                        error: errorItems,
                        other: otherItems
                    }
                });

                // Salva gli elementi Available e Orderable nel catalog order ONLY if we have an order ID and not already saving
                if (catalogOrderId && data.results.length > 0 && !savingToOrder) {
                    setSavingToOrder(true); // Prevent multiple calls
                    try {
                        await updateCatalogOrderWithAvailableAndOrderableItems(catalogOrderId, data.results);
                    } catch (error) {
                        console.error('❌ Error saving items to catalog order:', error);
                        message.error('Error saving items to catalog order');
                        setFileProcessed(false);
                        return;
                    } finally {
                        setSavingToOrder(false); // Reset the flag
                    }
                }
            }
            // Mantieni la compatibilità con il formato precedente se non ci sono results
            else if (data.items) {
                setUploadedData(data.items);

                // Calculate the summary
                const validItems = data.items.filter(item => item.valid || item.result === 'found' || item.result === 'orderable');
                const invalidItems = data.items.filter(item => !item.valid && item.result !== 'found' && item.result !== 'orderable');

                setSummary({
                    total: data.items.length,
                    valid: validItems.length,
                    invalid: invalidItems.length,
                    details: invalidItems.map(item => ({
                        sku: item.skuCode || item.sku,
                        name: item.name || 'Unknown',
                        error: item.error || 'Invalid code'
                    }))
                });
            }

            message.success('File processed successfully!');

            // Vai automaticamente al secondo step dopo l'elaborazione riuscita
            setTimeout(() => {
                setCurrentStep(1);
            }, 500);
        } else {
            setFileProcessed(false);
            message.error('Error processing file. Please check the console for details.');
        }
    };

    // Function to handle modal close attempt
    const handleCloseAttempt = () => {
        // Verifica se ci sono dati non salvati (file caricato, step > 0, ecc.)
        const hasUnsavedData = fileList.length > 0 || currentStep > 0 || uploadedData.length > 0 || catalogOrderId;

        if (hasUnsavedData) {
            // Chiudi altre modali prima di aprire quella di conferma chiusura
            setConfirmAddVisible(false);
            setSuccessModalVisible(false);
            setConfirmCloseVisible(true);
        } else {
            handleConfirmClose();
        }
    };

    // Function to handle confirmed close
    const handleConfirmClose = () => {
        // Nascondi immediatamente il modal principale
        setInternalVisible(false);
        setConfirmCloseVisible(false);

        // Reset completo di tutti i dati
        setCurrentStep(0);
        setFileList([]);
        setUploadedData([]);
        setSummary({
            total: 0,
            valid: 0,
            invalid: 0,
            details: []
        });
        setSelectedCatalogue(initialCatalogId);
        setSelectedLicense(null);
        setProcessingUpload(false);
        setParsingFile(false);
        setFileProcessed(false);
        setCatalogOrderId(null);
        // Reset selections
        setSelectedRowKeys([]);
        setSelectedProductsCount(0);
        setSelectedProducts([]);
        // Reset saving flag
        setSavingToOrder(false);

        // Use onDirectClose if available to avoid triggering close logic again, otherwise use onCancel
        if (onDirectClose) {
            onDirectClose();
        } else {
            onCancel();
        }
    };

    // Function to cancel the close operation
    const handleCancelClose = () => {
        setConfirmCloseVisible(false);
        // Il modal principale tornerà visibile automaticamente grazie a visible && !confirmCloseVisible
    };

    // Function to process file from button click
    const handleProcessFile = async () => {
        if (!fileList.length) {
            message.error('Please upload a file first');
            return;
        }

        // Trigger the file processing from UploadFileStep
        if (uploadStepRef.current && uploadStepRef.current.processFile) {
            uploadStepRef.current.processFile();
        }
    };

    // Function to proceed to the next step
    const nextStep = async () => {
        try {
            // Semplicemente vai al prossimo step
            // La logica di salvataggio dei dati è stata spostata in handleFileProcessed
            setCurrentStep(currentStep + 1);
        } catch (error) {
            console.error('Validation failed:', error);
            message.error('Please fix the validation errors before proceeding');
        }
    };

    // Function to go back to the previous step
    const prevStep = () => {
        setCurrentStep(currentStep - 1);
    };

    // Function to show confirmation modal
    const handleFinish = () => {
        if (!selectedCatalogue) {
            message.error('Please select a catalogue before proceeding');
            return;
        }

        if (selectedProductsCount === 0) {
            message.error('Please select at least one product to add');
            return;
        }

        // Chiudi altre modali prima di aprire quella di conferma aggiunta
        setConfirmCloseVisible(false);
        setSuccessModalVisible(false);
        setConfirmAddVisible(true);
    };

    // Function to complete the process after confirmation
    const handleConfirmAdd = async () => {
        setProcessingUpload(true);

        // Salva i dati necessari prima del reset
        const currentCatalogOrderId = catalogOrderId;
        const currentSelectedProducts = [...selectedProducts];
        const currentSelectedCatalogue = selectedCatalogue;
        const currentSelectedProductsCount = selectedProductsCount;

        // NON chiudere la modale di conferma ancora - aspettiamo l'esito dell'operazione

        // Validate required data first
        if (!currentCatalogOrderId || !currentSelectedProducts.length || !currentSelectedCatalogue) {
            message.error('Missing required data for catalog order update');
            setProcessingUpload(false);
            return;
        }

        try {
            // Step 1: Update the catalog order with final selections and status
            await updateCatalogOrderForAddition(currentCatalogOrderId, currentSelectedProducts, currentSelectedCatalogue);

            // SOLO ADESSO che l'operazione è andata a buon fine, chiudi la modale di conferma e mostra quella di successo
            setConfirmAddVisible(false);
            setConfirmCloseVisible(false);
            setSuccessModalProductsCount(currentSelectedProductsCount);
            setSuccessModalVisible(true);
            setProcessingUpload(false);

            // Step 2: Call Firebase function in background after showing success modal
            setTimeout(async () => {
                try {
                    const processCatalogOrderUrl = process.env.REACT_APP_PROCESS_CATALOG_ORDER;

                    if (!processCatalogOrderUrl) {
                        console.error('Firebase function URL not configured in environment variables');
                        return;
                    }

                    const response = await fetch(processCatalogOrderUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            orderId: currentCatalogOrderId
                        })
                    });

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error(`Firebase function failed with status ${response.status}: ${errorText}`);
                        return;
                    }
                } catch (error) {
                    console.error('Error calling Firebase function:', error);
                }
            }, 100); // Small delay to ensure modal is shown first

        } catch (error) {
            console.error('Error updating catalog order:', error);
            message.error('Failed to update catalog order. Please try again.');
            setProcessingUpload(false);
            // NON chiudere la modale di conferma in caso di errore - l'utente può riprovare
            return;
        }
    };

    // Function to cancel the add operation
    const handleCancelAdd = () => {
        setConfirmAddVisible(false);
    };

    // Function to handle success modal close
    const handleSuccessModalClose = () => {
        setSuccessModalVisible(false);
        setSuccessModalProductsCount(0);

        // Reset completo dello stato e chiusura del modal principale
        setInternalVisible(false);
        setConfirmAddVisible(false);
        setConfirmCloseVisible(false);

        // Reset completo dello stato
        setCurrentStep(0);
        setFileList([]);
        setUploadedData([]);
        setSummary({
            total: 0,
            valid: 0,
            invalid: 0,
            details: []
        });
        setSelectedCatalogue(initialCatalogId);
        setSelectedLicense(null);
        setFileProcessed(false);
        setCatalogOrderId(null);
        setSelectedProductsCount(0);
        setSelectedProducts([]);
        setSelectedRowKeys([]);

        // Prima chiama le funzioni di cleanup del parent se disponibili
        if (onDirectClose) {
            onDirectClose();
        } else if (onCancel) {
            onCancel();
        }
    };

    // Function to get row key for items - deve essere identica a quella in SelectAddStep
    const getRowKey = (record) => {
        return record.skuCode || record.eanCode || record.upcCode || `item-${Math.random()}`;
    };

    // Function to handle selection changes from SelectAddStep
    const handleSelectionChange = useCallback((newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);

        // Calcola i prodotti selezionati basandosi sui dati disponibili
        const availableItems = summary.details?.available || [];
        const selectedItems = availableItems.filter(item => {
            const key = getRowKey(item);
            return newSelectedRowKeys.includes(key);
        });

        setSelectedProductsCount(selectedItems.length);
        setSelectedProducts(selectedItems);

    }, [summary.details?.available]);

    // Effect per inizializzare le selezioni quando i dati sono disponibili
    useEffect(() => {
        if (summary.details?.available && summary.details.available.length > 0) {
            // Inizializza le selezioni basandosi su isSelected
            const initialSelectedKeys = summary.details.available
                .filter(item => item.isSelected === true)
                .map(item => getRowKey(item));

            setSelectedRowKeys(initialSelectedKeys);

            // Aggiorna anche il conteggio
            const selectedItems = summary.details.available.filter(item => item.isSelected === true);
            setSelectedProductsCount(selectedItems.length);
            setSelectedProducts(selectedItems);
        }
    }, [summary.details?.available]);

    // Ref per il componente UploadFileStep
    const uploadStepRef = useRef(null);

    // Function to handle Order Assets navigation
    const handleOrderAssets = () => {
        if (!summary.details?.orderable || summary.details.orderable.length === 0) {
            message.warning('No orderable products available');
            return;
        }

        // Prepare data for the BulkOrderModal
        const orderableData = {
            results: summary.details.orderable,
            summary: {
                total: summary.details.orderable.length,
                orderable: summary.details.orderable.length,
                error: 0,
                inProgress: 0,
                details: {
                    orderable: summary.details.orderable,
                    error: [],
                    inProgress: []
                }
            }
        };

        // Store data in sessionStorage for transfer
        sessionStorage.setItem('bulkOrderData', JSON.stringify(orderableData));
        sessionStorage.setItem('openBulkOrderModal', 'true');

        // Navigate to orders page
        navigate('/orderpage');
    };

    // Content for each step
    const steps = [
        {
            title: 'Upload File',
            content: <UploadFileStep
                ref={uploadStepRef}
                uploadProps={uploadProps}
                parsingFile={parsingFile}
                onFileProcessed={handleFileProcessed}
                onFileUploaded={handleFileUploaded}
            />
        },
        {
            title: 'Review Summary',
            content: <ReviewSummaryStep
                summary={summary}
                uploadedData={uploadedData}
                parsingFile={parsingFile}
            />
        },
        {
            title: 'Select & Add',
            content: (
                <SelectAddStep
                    catalogues={catalogues}
                    selectedCatalogue={selectedCatalogue}
                    setSelectedCatalogue={setSelectedCatalogue}
                    selectedLicense={selectedLicense}
                    setSelectedLicense={setSelectedLicense}
                    loading={loading}
                    availableItemsData={summary.details?.available || []}
                    selectedRowKeys={selectedRowKeys}
                    onSelectionChange={handleSelectionChange}
                    summary={summary}
                    initialCatalogId={initialCatalogId}
                />
            )
        }
    ];

    // Bulk Add Stepper component
    const BulkAddStepper = () => (
        <div style={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: '1.5rem', height: '35rem', width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                {/* Left section with steps */}
                <div style={{ width: '15.625rem', flexShrink: 0, borderRight: '1px solid #f0f0f0', paddingRight: '1rem' }}>
                    <Steps current={currentStep} direction="vertical" style={{ height: '100%' }}>
                        {steps.map(item => (
                            <Step key={item.title} title={item.title} />
                        ))}
                    </Steps>
                </div>

                {/* Main content */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', width: 'calc(100% - 17rem)', boxSizing: 'border-box', overflow: 'hidden' }}>
                    <div className="steps-content" style={{ flex: 1, overflowY: 'auto', padding: '0.5rem', width: '100%', boxSizing: 'border-box' }}>
                        {steps[currentStep].content}
                    </div>
                </div>
            </div>

            <div className="steps-action" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
                {currentStep > 0 && (
                    <Button onClick={prevStep} disabled={processingUpload}>
                        Previous
                    </Button>
                )}

                {currentStep === 0 && (
                    <>
                        <Button onClick={handleCloseAttempt}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleProcessFile}
                            disabled={!fileList.length || parsingFile || processingUpload}
                            loading={parsingFile}
                        >
                            Process File
                        </Button>
                    </>
                )}

                {currentStep === 1 && (
                    <Button
                        type="primary"
                        onClick={nextStep}
                        disabled={parsingFile || processingUpload}
                    >
                        Next
                    </Button>
                )}

                {currentStep === steps.length - 1 && (
                    <Button
                        type="primary"
                        onClick={handleFinish}
                        loading={processingUpload}
                        disabled={!selectedCatalogue || selectedProductsCount === 0 || processingUpload}
                    >
                        Add to Catalogue
                    </Button>
                )}
            </div>
        </div>
    );

    // Get selected catalogue name
    const selectedCatalogueName = catalogues.find(cat => cat.id === selectedCatalogue)?.nameCatalog || '';

    return (
        <>
            <StepperModal
                title="Bulk Add Assets to a Catalogue"
                visible={visible && internalVisible && !confirmCloseVisible && !confirmAddVisible && !successModalVisible}
                onCancel={handleCloseAttempt}
            >
                <BulkAddStepper />
            </StepperModal>

            {/* Modale di conferma chiusura - indipendente */}
            <ConfirmCloseModal
                visible={confirmCloseVisible && !confirmAddVisible && !successModalVisible}
                onCancel={handleCancelClose}
                onConfirm={handleConfirmClose}
            />

            {/* Modale di conferma aggiunta - indipendente */}
            <ConfirmAddModal
                visible={confirmAddVisible && !confirmCloseVisible && !successModalVisible}
                onCancel={handleCancelAdd}
                onConfirm={handleConfirmAdd}
                selectedCount={selectedProductsCount}
                catalogueName={selectedCatalogueName}
                loading={processingUpload}
            />

            {/* Modale di successo - indipendente */}
            <SuccessProcessingModal
                visible={successModalVisible && !confirmCloseVisible && !confirmAddVisible}
                onClose={handleSuccessModalClose}
                productsCount={successModalProductsCount}
                orderableCount={summary.orderable || 0}
                onOrderAssets={handleOrderAssets}
            />
        </>
    );
};

export default BulkAddModal; 